import {
  Breadcrumb,
  Button,
  Checkbox,
  Input,
  Modal,
  message,
  Radio,
  Space,
  Pagination,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import {
  InboxOutlined,
  CheckOutlined,
  CopyOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FolderAddOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  HomeOutlined,
  LinkOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  SyncOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Api,
  getBaseURL,
  ICloudFile,
  ICloudFolder,
  IMediaFile,
  IMediaFolder,
} from "@model";
import { useCallback, useEffect, useRef, useState } from "react";
import { useIsCompact } from "../../hooks/useIsCompact";

const { Dragger } = Upload;

const COMPRESS_QUALITY = 0.95;

async function compressImageFile(file: File): Promise<File> {
  if (
    !file.type.startsWith("image/") ||
    file.type === "image/svg+xml" ||
    file.type === "image/gif"
  ) {
    return file;
  }

  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const baseName = file.name.replace(/\.[^.]+$/, "");
          resolve(new File([blob], `${baseName}.jpg`, { type: "image/jpeg" }));
        },
        "image/jpeg",
        COMPRESS_QUALITY,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });
}

const getApiBase = () => getBaseURL() ?? "";

export const buildMediaFullUrl = (relativeUrl: string) =>
  `${getApiBase()}/${relativeUrl}`;

export type AdminFileManagerProps = {
  defaultFolder?: string;
  onSelectUrl?: (fullUrl: string) => void;
};

const isImageFile = (mimeType?: string, url?: string) =>
  mimeType?.startsWith("image/") ||
  /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(url ?? "");

const isAudioFile = (mimeType?: string, url?: string) =>
  mimeType?.startsWith("audio/") ||
  /\.(mp3|wav|ogg|m4a|aac|webm|flac)$/i.test(url ?? "");

const isVideoFile = (mimeType?: string, url?: string) =>
  mimeType?.startsWith("video/") ||
  /\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(url ?? "");

function MediaPreview({ record }: { record: IMediaFile }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const url = record.url ? buildMediaFullUrl(record.url) : "";

  if (!url) return <>-</>;

  if (isVideoFile(record.mimeType, record.url)) {
    const togglePlayback = () => {
      const video = videoRef.current;
      if (!video) return;
      if (playing) {
        video.pause();
        setPlaying(false);
        return;
      }
      void video
        .play()
        .then(() => setPlaying(true))
        .catch(() => {
          message.error("Could not play video");
          setPlaying(false);
        });
    };

    return (
      <div style={{ position: "relative", width: 48, height: 48 }}>
        <video
          ref={videoRef}
          src={url}
          preload="metadata"
          muted
          style={{
            width: 48,
            height: 48,
            objectFit: "cover",
            borderRadius: 6,
            display: "block",
            background: "#000",
          }}
          onEnded={() => setPlaying(false)}
          onPause={() => setPlaying(false)}
        />
        <Button
          type="text"
          size="small"
          icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
          aria-label={playing ? "Pause" : "Play"}
          onClick={togglePlayback}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 48,
            height: 48,
            color: "#fff",
          }}
        />
      </div>
    );
  }

  if (isImageFile(record.mimeType, record.url)) {
    return (
      <>
        <img
          src={url}
          alt={record.originalName ?? "Preview"}
          onClick={() => setZoomOpen(true)}
          style={{
            width: 48,
            height: 48,
            objectFit: "cover",
            borderRadius: 6,
            display: "block",
            cursor: "zoom-in",
          }}
        />
        <Modal
          open={zoomOpen}
          footer={null}
          closable={false}
          centered
          width="min(416px, 96vw)"
          bodyStyle={{ padding: 16 }}
          onCancel={() => setZoomOpen(false)}
        >
          <img
            src={url}
            alt={record.originalName ?? "Preview"}
            onClick={() => setZoomOpen(false)}
            style={{
              width: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
              display: "block",
              cursor: "zoom-out",
            }}
          />
        </Modal>
      </>
    );
  }

  if (isAudioFile(record.mimeType, record.url)) {
    const togglePlayback = () => {
      const audio = audioRef.current;
      if (!audio) return;
      if (playing) {
        audio.pause();
        setPlaying(false);
        return;
      }
      void audio
        .play()
        .then(() => setPlaying(true))
        .catch(() => {
          message.error("Could not play audio");
          setPlaying(false);
        });
    };

    return (
      <>
        <Button
          type="text"
          size="small"
          icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
          aria-label={playing ? "Pause" : "Play"}
          onClick={togglePlayback}
        />
        <audio
          ref={audioRef}
          src={url}
          preload="none"
          onEnded={() => setPlaying(false)}
          onPause={() => setPlaying(false)}
        />
      </>
    );
  }

  return <>-</>;
}

export default function AdminFileManager({
  defaultFolder = "",
  onSelectUrl,
}: AdminFileManagerProps) {
  const [currentPath, setCurrentPath] = useState(defaultFolder);
  const [folders, setFolders] = useState<IMediaFolder[]>([]);
  const [files, setFiles] = useState<IMediaFile[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filePage, setFilePage] = useState(1);
  const isCompact = useIsCompact();
  const [uploadingCount, setUploadingCount] = useState(0);
  const uploadBatchRef = useRef<File[]>([]);
  const uploadBatchTimerRef = useRef<number>();
  const rawBatchRef = useRef<File[]>([]);
  const rawBatchTimerRef = useRef<number>();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [folderNameInput, setFolderNameInput] = useState("");
  const [folderToRename, setFolderToRename] = useState<IMediaFolder | null>(
    null,
  );

  const [cloudModalOpen, setCloudModalOpen] = useState(false);
  const [cloudLoading, setCloudLoading] = useState(false);
  const [cloudFiles, setCloudFiles] = useState<ICloudFile[]>([]);
  const [cloudFolders, setCloudFolders] = useState<ICloudFolder[]>([]);
  const [cloudPath, setCloudPath] = useState("");
  const [excludeChat, setExcludeChat] = useState(false);
  const [cloudFilter, setCloudFilter] = useState<"all" | "inDb" | "notInDb">(
    "all",
  );
  const [cloudPage, setCloudPage] = useState(1);

  const visibleCloudFiles = excludeChat
    ? cloudFiles.filter(
        (f) => !f.key.toLowerCase().split("/").slice(0, -1).includes("chat"),
      )
    : cloudFiles;

  const filteredCloudFiles = visibleCloudFiles.filter((file) =>
    cloudFilter === "all" ? true : cloudFilter === "inDb" ? file.inDb : !file.inDb,
  );

  const isInsideFolder = currentPath.length > 0;
  const uploading = uploadingCount > 0;

  const fetchFolders = useCallback(async (parentPath: string) => {
    setFoldersLoading(true);
    try {
      const res = await Api.get("/api/admin/panel/files/folders", {
        params: parentPath ? { parent: parentPath } : undefined,
      });
      setFolders(res.data?.data ?? []);
    } catch {
      message.error("Failed to load folders");
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  }, []);

  const fetchFiles = useCallback(async (folderPath: string) => {
    if (!folderPath) {
      setFiles([]);
      return;
    }
    setFilesLoading(true);
    try {
      const res = await Api.get("/api/admin/panel/files", {
        params: { folder: folderPath },
      });
      setFiles(res.data?.data ?? []);
    } catch {
      message.error("Failed to load files");
      setFiles([]);
    } finally {
      setFilesLoading(false);
    }
  }, []);

  const loadCloud = useCallback(async (path: string) => {
    setCloudModalOpen(true);
    setCloudLoading(true);
    try {
      const res = await Api.get("/api/admin/panel/files/cloud", {
        params: path ? { path } : undefined,
      });
      setCloudPath(path);
      setCloudFiles(res.data?.data?.files ?? []);
      setCloudFolders(res.data?.data?.folders ?? []);
    } catch {
      message.error("Failed to sync files from cloud");
      setCloudFiles([]);
      setCloudFolders([]);
    } finally {
      setCloudLoading(false);
    }
  }, []);

  const syncCloudFiles = () => loadCloud(cloudPath);

  const importCloudFile = async (record: ICloudFile) => {
    try {
      await Api.post("/api/admin/panel/files/cloud/import", {
        key: record.key,
      });
      message.success("Added to database");
      void syncCloudFiles();
      refresh();
    } catch {
      message.error("Failed to add to database");
    }
  };

  const deleteCloudFile = (record: ICloudFile) => {
    Modal.confirm({
      title: "Delete file",
      icon: <ExclamationCircleOutlined />,
      content: `Delete "${record.key}" from storage${record.inDb ? " and database" : ""}?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await Api.delete("/api/admin/panel/files/cloud", {
            params: { key: record.key },
          });
          message.success("File deleted");
          void syncCloudFiles();
          refresh();
        } catch {
          message.error("Failed to delete file");
        }
      },
    });
  };

  const uploadSingleFile = useCallback(
    async (file: File, raw: boolean) => {
      const fileToUpload = raw ? file : await compressImageFile(file);
      const formData = new FormData();
      formData.append("file", fileToUpload);
      await Api.post(
        `/api/admin/panel/files/upload?folder=${encodeURIComponent(currentPath)}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
    },
    [currentPath],
  );

  const uploadFiles = useCallback(
    async (fileList: File[], raw: boolean) => {
      if (!isInsideFolder) {
        message.warning("Open a folder before uploading");
        return;
      }
      if (fileList.length === 0) return;

      setUploadingCount((count) => count + 1);
      let success = 0;
      let failed = 0;
      try {
        for (const file of fileList) {
          try {
            await uploadSingleFile(file, raw);
            success += 1;
          } catch {
            failed += 1;
          }
        }
        if (success > 0) {
          message.success(
            success === 1 ? "File uploaded" : `${success} files uploaded`,
          );
          fetchFiles(currentPath);
          fetchFolders(currentPath);
        }
        if (failed > 0) {
          message.error(
            failed === 1
              ? "1 file failed to upload"
              : `${failed} files failed to upload`,
          );
        }
      } finally {
        setUploadingCount((count) => Math.max(0, count - 1));
      }
    },
    [currentPath, fetchFiles, fetchFolders, isInsideFolder, uploadSingleFile],
  );

  const queueUpload = useCallback(
    (file: File, raw: boolean) => {
      const batchRef = raw ? rawBatchRef : uploadBatchRef;
      const timerRef = raw ? rawBatchTimerRef : uploadBatchTimerRef;
      batchRef.current.push(file);
      window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        const batch = batchRef.current.splice(0);
        void uploadFiles(batch, raw);
      }, 0);
    },
    [uploadFiles],
  );

  const handleBeforeUpload = useCallback(
    (file: File) => {
      queueUpload(file, false);
      return false;
    },
    [queueUpload],
  );

  const handleBeforeUploadRaw = useCallback(
    (file: File) => {
      queueUpload(file, true);
      return false;
    },
    [queueUpload],
  );

  useEffect(
    () => () => {
      window.clearTimeout(uploadBatchTimerRef.current);
      window.clearTimeout(rawBatchTimerRef.current);
    },
    [],
  );

  const refresh = useCallback(() => {
    void fetchFolders(currentPath);
    void fetchFiles(currentPath);
  }, [currentPath, fetchFiles, fetchFolders]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    setFilePage(1);
  }, [currentPath]);

  const handleDeleteFile = (record: IMediaFile) => {
    Modal.confirm({
      title: "Delete file",
      icon: <ExclamationCircleOutlined />,
      content: `Delete "${record.originalName}"?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await Api.delete(`/api/admin/panel/files/${record._id}`);
          message.success("File deleted");
          fetchFiles(currentPath);
        } catch {
          message.error("Failed to delete file");
        }
      },
    });
  };

  const handleCreateFolder = async () => {
    const name = folderNameInput.trim();
    if (!name) {
      message.warning("Enter a folder name");
      return;
    }
    try {
      await Api.post("/api/admin/panel/files/folders", {
        parentPath: currentPath || null,
        name,
      });
      message.success("Folder created");
      setCreateModalOpen(false);
      setFolderNameInput("");
      fetchFolders(currentPath);
    } catch {
      message.error("Failed to create folder");
    }
  };

  const handleRenameFolder = async () => {
    if (!folderToRename) return;
    const name = folderNameInput.trim();
    if (!name) {
      message.warning("Enter a folder name");
      return;
    }
    try {
      await Api.patch("/api/admin/panel/files/folders", {
        path: folderToRename.path,
        name,
      });
      message.success("Folder renamed");
      setRenameModalOpen(false);
      setFolderToRename(null);
      setFolderNameInput("");
      refresh();
    } catch {
      message.error("Failed to rename folder");
    }
  };

  const handleDeleteFolder = (folder: IMediaFolder) => {
    Modal.confirm({
      title: "Delete folder",
      icon: <ExclamationCircleOutlined />,
      content: `Delete "${folder.name}"? It must be empty.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await Api.delete("/api/admin/panel/files/folders", {
            params: { path: folder.path },
          });
          message.success("Folder deleted");
          fetchFolders(currentPath);
        } catch {
          message.error("Failed to delete folder");
        }
      },
    });
  };

  const copyUrl = (url: string) => {
    const full = buildMediaFullUrl(url);
    navigator.clipboard
      .writeText(full)
      .then(() => message.success("URL copied"));
  };

  const pathSegments = currentPath.split("/").filter(Boolean);

  return (
    <>
      {isCompact && (
        <style>{`.file-manager-upload-full .ant-upload-select{display:block;width:100%;}`}</style>
      )}
      <Breadcrumb style={{ marginBottom: 12 }}>
        <Breadcrumb.Item>
          <Button
            type="link"
            size="small"
            icon={<HomeOutlined />}
            onClick={() => setCurrentPath("")}
            style={{ padding: 0 }}
          >
            Home
          </Button>
        </Breadcrumb.Item>
        {pathSegments.map((segment, index) => {
          const path = pathSegments.slice(0, index + 1).join("/");
          const isLast = index === pathSegments.length - 1;
          return (
            <Breadcrumb.Item key={path}>
              {isLast ? (
                segment
              ) : (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setCurrentPath(path)}
                  style={{ padding: 0 }}
                >
                  {segment}
                </Button>
              )}
            </Breadcrumb.Item>
          );
        })}
      </Breadcrumb>

      <div
        style={{
          display: "flex",
          flexDirection: isCompact ? "column" : "row",
          flexWrap: "wrap",
          gap: 8,
          alignItems: isCompact ? "stretch" : "center",
          marginBottom: 16,
        }}
      >
        <Button
          block={isCompact}
          icon={<FolderAddOutlined />}
          onClick={() => {
            setFolderNameInput("");
            setCreateModalOpen(true);
          }}
        >
          New folder
        </Button>
        <Upload
          className={isCompact ? "file-manager-upload-full" : undefined}
          beforeUpload={handleBeforeUpload}
          showUploadList={false}
          style={isCompact ? { display: "block", width: "100%" } : undefined}
        >
          <Button
            block={isCompact}
            icon={<UploadOutlined />}
            loading={uploading}
            type="primary"
            disabled={!isInsideFolder}
          >
            Upload (compressed)
          </Button>
        </Upload>
        <Upload
          className={isCompact ? "file-manager-upload-full" : undefined}
          beforeUpload={handleBeforeUploadRaw}
          showUploadList={false}
          style={isCompact ? { display: "block", width: "100%" } : undefined}
        >
          <Button
            block={isCompact}
            icon={<UploadOutlined />}
            loading={uploading}
            disabled={!isInsideFolder}
          >
            Upload raw
          </Button>
        </Upload>
        <Button block={isCompact} icon={<SyncOutlined />} onClick={syncCloudFiles}>
          Sync from cloud
        </Button>
        {!isInsideFolder && (
          <Typography.Text type="secondary">
            Open a folder to upload files
          </Typography.Text>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isCompact ? "column" : "row",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ flex: "1 1 0", minWidth: 0 }}>
          <Dragger
            multiple
            showUploadList={false}
            disabled={!isInsideFolder || uploading}
            beforeUpload={handleBeforeUpload}
            style={{ opacity: isInsideFolder ? 1 : 0.65 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {isInsideFolder
                ? "Drag here — compressed"
                : "Open a folder first"}
            </p>
            <p className="ant-upload-hint">Images converted to JPEG 95%</p>
          </Dragger>
        </div>

        <div style={{ flex: "1 1 0", minWidth: 0 }}>
          <Dragger
            multiple
            showUploadList={false}
            disabled={!isInsideFolder || uploading}
            beforeUpload={handleBeforeUploadRaw}
            style={{ opacity: isInsideFolder ? 1 : 0.65 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {isInsideFolder ? "Drag here — raw" : "Open a folder first"}
            </p>
            <p className="ant-upload-hint">No conversion, exact file as-is</p>
          </Dragger>
        </div>
      </div>

      {isCompact ? (
        <Spin spinning={foldersLoading}>
          {folders.length === 0 && !foldersLoading ? (
            <div style={{ textAlign: "center", color: "#888", padding: 16 }}>No folders</div>
          ) : (
            folders.map((record) => (
              <div
                key={record.path}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  padding: "8px 12px",
                  marginBottom: 8,
                }}
              >
                <Button
                  type="link"
                  icon={<FolderOutlined />}
                  onClick={() => setCurrentPath(record.path)}
                  style={{ padding: 0, flex: 1, minWidth: 0, textAlign: "left", whiteSpace: "normal", height: "auto" }}
                >
                  <span style={{ wordBreak: "break-word" }}>{record.name}</span>
                </Button>
                {!record.isSystem && (
                  <Space size={0}>
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setFolderToRename(record);
                        setFolderNameInput(record.name);
                        setRenameModalOpen(true);
                      }}
                    />
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteFolder(record)}
                    />
                  </Space>
                )}
              </div>
            ))
          )}
        </Spin>
      ) : (
      <Table
        loading={foldersLoading}
        size="small"
        rowKey={(r) => r.path}
        dataSource={folders}
        pagination={false}
        style={{ marginBottom: 16 }}
        locale={{ emptyText: "No folders" }}
        onRow={(record) => ({
          onDoubleClick: () => setCurrentPath(record.path),
        })}
        columns={[
          {
            title: "Folder",
            dataIndex: "name",
            key: "name",
            render: (name: string, record: IMediaFolder) => (
              <Button
                type="link"
                icon={<FolderOutlined />}
                onClick={() => setCurrentPath(record.path)}
                style={{ padding: 0 }}
              >
                {name}
              </Button>
            ),
          },
          {
            title: "",
            key: "folderActions",
            width: 100,
            render: (_: unknown, record: IMediaFolder) =>
              record.isSystem ? null : (
                <Space size={0}>
                  <Tooltip title="Rename">
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setFolderToRename(record);
                        setFolderNameInput(record.name);
                        setRenameModalOpen(true);
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Delete">
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteFolder(record)}
                    />
                  </Tooltip>
                </Space>
              ),
          },
        ]}
      />
      )}

      {isInsideFolder && isCompact && (
        <Spin spinning={filesLoading}>
          {files.length === 0 ? (
            <div style={{ textAlign: "center", color: "#888", padding: 16 }}>
              No files in this folder
            </div>
          ) : (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {files
                  .slice((filePage - 1) * 12, filePage * 12)
                  .map((record) => {
                    const fileUrl = record.url ?? "";
                    return (
                    <div
                      key={record._id}
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        padding: 8,
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                      }}
                    >
                      <MediaPreview record={record} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, wordBreak: "break-word" }}>
                          {record.originalName}
                        </div>
                        <div style={{ fontSize: 12, color: "#888" }}>
                          {record.mimeType}
                          {record.size
                            ? ` · ${(record.size / 1024).toFixed(1)} KB`
                            : ""}
                        </div>
                        <Space size={0} wrap>
                          {onSelectUrl && (
                            <Button
                              type="link"
                              size="small"
                              icon={<CheckOutlined />}
                              onClick={() =>
                                onSelectUrl(buildMediaFullUrl(fileUrl))
                              }
                            >
                              Select
                            </Button>
                          )}
                          <Button
                            type="link"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => copyUrl(fileUrl)}
                          />
                          <Button
                            type="link"
                            size="small"
                            icon={<LinkOutlined />}
                            onClick={() =>
                              window.open(buildMediaFullUrl(fileUrl), "_blank")
                            }
                          />
                          <Button
                            type="link"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteFile(record)}
                          />
                        </Space>
                      </div>
                    </div>
                    );
                  })}
              </div>
              {files.length > 12 && (
                <Pagination
                  style={{ marginTop: 12, textAlign: "center" }}
                  size="small"
                  simple
                  current={filePage}
                  pageSize={12}
                  total={files.length}
                  onChange={setFilePage}
                />
              )}
            </>
          )}
        </Spin>
      )}

      {isInsideFolder && !isCompact && (
        <Table
          loading={filesLoading}
          size="small"
          rowKey={(r) => r._id ?? ""}
          dataSource={files}
          locale={{ emptyText: "No files in this folder" }}
          columns={[
            {
              title: "Preview",
              key: "preview",
              width: 72,
              render: (_: unknown, record: IMediaFile) => (
                <MediaPreview record={record} />
              ),
            },
            {
              title: "Name",
              dataIndex: "originalName",
              key: "originalName",
              ellipsis: true,
            },
            {
              title: "Type",
              dataIndex: "mimeType",
              key: "mimeType",
              width: 140,
              render: (t: string) => <Tag>{t}</Tag>,
            },
            {
              title: "Size",
              dataIndex: "size",
              key: "size",
              width: 90,
              render: (s: number) => (s ? `${(s / 1024).toFixed(1)} KB` : "-"),
            },
            {
              title: "Date",
              dataIndex: "uploadDate",
              key: "uploadDate",
              width: 160,
              render: (d: string) => (d ? new Date(d).toLocaleString() : "-"),
            },
            {
              title: "URL",
              dataIndex: "url",
              key: "url",
              render: (url: string) => (
                <Space>
                  <Typography.Text
                    style={{ maxWidth: 240, fontSize: 12 }}
                    ellipsis={{ tooltip: buildMediaFullUrl(url) }}
                  >
                    {url}
                  </Typography.Text>
                  {onSelectUrl && (
                    <Tooltip title="Select file">
                      <Button
                        type="link"
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={() => onSelectUrl(buildMediaFullUrl(url))}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title="Copy full URL">
                    <Button
                      type="link"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyUrl(url)}
                    />
                  </Tooltip>
                  <Tooltip title="Open in new tab">
                    <Button
                      type="link"
                      size="small"
                      icon={<LinkOutlined />}
                      onClick={() =>
                        window.open(buildMediaFullUrl(url), "_blank")
                      }
                    />
                  </Tooltip>
                </Space>
              ),
            },
            {
              title: "",
              key: "actions",
              width: 60,
              render: (_: unknown, record: IMediaFile) => (
                <Button
                  type="link"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteFile(record)}
                />
              ),
            },
          ]}
        />
      )}

      <Modal
        title="Cloud files"
        open={cloudModalOpen}
        width="min(900px, 96vw)"
        footer={<Button onClick={() => setCloudModalOpen(false)}>Close</Button>}
        onCancel={() => setCloudModalOpen(false)}
      >
        <Breadcrumb style={{ marginBottom: 12 }}>
          <Breadcrumb.Item>
            <Button
              type="link"
              size="small"
              icon={<HomeOutlined />}
              onClick={() => loadCloud("")}
              style={{ padding: 0 }}
            >
              Bucket
            </Button>
          </Breadcrumb.Item>
          {cloudPath
            .split("/")
            .filter(Boolean)
            .map((segment, index, all) => {
              const path = all.slice(0, index + 1).join("/");
              return (
                <Breadcrumb.Item key={path}>
                  {index === all.length - 1 ? (
                    segment
                  ) : (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => loadCloud(path)}
                      style={{ padding: 0 }}
                    >
                      {segment}
                    </Button>
                  )}
                </Breadcrumb.Item>
              );
            })}
        </Breadcrumb>
        <Table
          loading={cloudLoading}
          size="small"
          rowKey="path"
          dataSource={cloudFolders}
          pagination={false}
          showHeader={false}
          style={{ marginBottom: 12 }}
          locale={{ emptyText: "No subfolders" }}
          columns={[
            {
              dataIndex: "name",
              key: "name",
              render: (name: string, record: ICloudFolder) => (
                <Button
                  type="link"
                  icon={<FolderOutlined />}
                  onClick={() => loadCloud(record.path)}
                  style={{ padding: 0 }}
                >
                  {name}
                </Button>
              ),
            },
          ]}
        />
        <Space
          direction={isCompact ? "vertical" : "horizontal"}
          style={{ marginBottom: 12, width: isCompact ? "100%" : undefined }}
          wrap
        >
          <Button
            block={isCompact}
            icon={<SyncOutlined />}
            loading={cloudLoading}
            onClick={syncCloudFiles}
          >
            Reload
          </Button>
          <Radio.Group
            value={cloudFilter}
            onChange={(e) => {
              setCloudFilter(e.target.value);
              setCloudPage(1);
            }}
            style={isCompact ? { display: "flex", width: "100%" } : undefined}
          >
            <Radio.Button value="all" style={isCompact ? { flex: 1, textAlign: "center" } : undefined}>
              All ({visibleCloudFiles.length})
            </Radio.Button>
            <Radio.Button value="inDb" style={isCompact ? { flex: 1, textAlign: "center" } : undefined}>
              In DB ({visibleCloudFiles.filter((f) => f.inDb).length})
            </Radio.Button>
            <Radio.Button value="notInDb" style={isCompact ? { flex: 1, textAlign: "center" } : undefined}>
              Not in DB ({visibleCloudFiles.filter((f) => !f.inDb).length})
            </Radio.Button>
          </Radio.Group>
          <Checkbox
            checked={excludeChat}
            onChange={(e) => setExcludeChat(e.target.checked)}
          >
            Exclude chat folder photos
          </Checkbox>
        </Space>
        {isCompact ? (
          <Spin spinning={cloudLoading}>
            {filteredCloudFiles.length === 0 && !cloudLoading ? (
              <div style={{ textAlign: "center", color: "#888", padding: 16 }}>
                No files in this folder
              </div>
            ) : (
              <>
                {filteredCloudFiles
                  .slice((cloudPage - 1) * 10, cloudPage * 10)
                  .map((record) => (
                    <div
                      key={record.key}
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                        padding: 8,
                        marginBottom: 8,
                      }}
                    >
                      <MediaPreview record={{ url: record.key, originalName: record.key }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, wordBreak: "break-all" }}>{record.key}</div>
                        <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                          {record.size ? `${(record.size / 1024).toFixed(1)} KB` : "-"}
                          {record.lastModified
                            ? ` · ${new Date(record.lastModified).toLocaleString()}`
                            : ""}
                        </div>
                        <div style={{ marginTop: 4 }}>
                          {record.inDb ? (
                            <Tag color="green">In DB</Tag>
                          ) : (
                            <Tag color="orange">Not in DB</Tag>
                          )}
                        </div>
                        <Space size={0}>
                          {!record.inDb && (
                            <Button
                              type="link"
                              size="small"
                              icon={<DatabaseOutlined />}
                              onClick={() => importCloudFile(record)}
                            />
                          )}
                          <Button
                            type="link"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => deleteCloudFile(record)}
                          />
                        </Space>
                      </div>
                    </div>
                  ))}
                {filteredCloudFiles.length > 10 && (
                  <Pagination
                    style={{ marginTop: 8, textAlign: "center" }}
                    size="small"
                    simple
                    current={cloudPage}
                    pageSize={10}
                    total={filteredCloudFiles.length}
                    onChange={setCloudPage}
                  />
                )}
              </>
            )}
          </Spin>
        ) : (
        <Table
          loading={cloudLoading}
          size="small"
          rowKey="key"
          dataSource={filteredCloudFiles}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          locale={{ emptyText: "No files in this folder" }}
          columns={[
            {
              title: "Preview",
              key: "preview",
              width: 72,
              render: (_: unknown, record: ICloudFile) => (
                <MediaPreview
                  record={{ url: record.key, originalName: record.key }}
                />
              ),
            },
            {
              title: "Key",
              dataIndex: "key",
              key: "key",
              ellipsis: true,
            },
            {
              title: "Size",
              dataIndex: "size",
              key: "size",
              width: 90,
              render: (s: number) => (s ? `${(s / 1024).toFixed(1)} KB` : "-"),
            },
            {
              title: "Date",
              dataIndex: "lastModified",
              key: "lastModified",
              width: 160,
              render: (d: string) => (d ? new Date(d).toLocaleString() : "-"),
            },
            {
              title: "Database",
              dataIndex: "inDb",
              key: "inDb",
              width: 110,
              render: (inDb: boolean) =>
                inDb ? (
                  <Tag color="green">In DB</Tag>
                ) : (
                  <Tag color="orange">Not in DB</Tag>
                ),
            },
            {
              title: "",
              key: "actions",
              width: 90,
              render: (_: unknown, record: ICloudFile) => (
                <Space size={0}>
                  {!record.inDb && (
                    <Tooltip title="Add to DB">
                      <Button
                        type="link"
                        size="small"
                        icon={<DatabaseOutlined />}
                        onClick={() => importCloudFile(record)}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title="Delete">
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => deleteCloudFile(record)}
                    />
                  </Tooltip>
                </Space>
              ),
            },
          ]}
        />
        )}
      </Modal>

      <Modal
        title="New folder"
        open={createModalOpen}
        okText="Create"
        onOk={handleCreateFolder}
        onCancel={() => {
          setCreateModalOpen(false);
          setFolderNameInput("");
        }}
      >
        <Input
          placeholder="Folder name"
          value={folderNameInput}
          onChange={(e) => setFolderNameInput(e.target.value)}
          onPressEnter={handleCreateFolder}
          prefix={<FolderOpenOutlined />}
        />
      </Modal>

      <Modal
        title="Rename folder"
        open={renameModalOpen}
        okText="Save"
        onOk={handleRenameFolder}
        onCancel={() => {
          setRenameModalOpen(false);
          setFolderToRename(null);
          setFolderNameInput("");
        }}
      >
        <Input
          placeholder="Folder name"
          value={folderNameInput}
          onChange={(e) => setFolderNameInput(e.target.value)}
          onPressEnter={handleRenameFolder}
          prefix={<EditOutlined />}
        />
      </Modal>
    </>
  );
}
